/**
 * Mental-health provider reference list, used to tell a mental-health setting
 * from an acute/general one.
 *
 * The pathway data carries no acute/MH flag and `trust_id` is an internal UUID
 * (not an ODS code), and trust *names* don't signal type (only a handful contain
 * "Mental Health"). So classification is by name against this curated list:
 *
 *  - the ~50 English NHS mental-health / community trusts that provide MHA
 *    inpatient detention (including recently-merged predecessors, kept so
 *    historical data still classifies),
 *  - the independent-sector MH hospital providers that take detained patients,
 *  - name variants as they appear in our data where they differ from the
 *    canonical name (e.g. "Kent and Medway Mental Health NHS Trust").
 *
 * Deliberately NOT included: acute / general / specialist hospital trusts, and
 * Welsh/Scottish health boards (combined acute+MH; out of the England scope and
 * would need their own handling). Matching strips case, diacritics, apostrophes
 * and other punctuation, so punctuation/spelling drift (missing apostrophes,
 * commas, accents, "and" vs "&") doesn't matter.
 */
export const MENTAL_HEALTH_TRUSTS: readonly string[] = [
  // Current English NHS mental-health / community trusts
  "Avon and Wiltshire Mental Health Partnership NHS Trust",
  "Berkshire Healthcare NHS Foundation Trust",
  "Birmingham and Solihull Mental Health NHS Foundation Trust",
  "Black Country Healthcare NHS Foundation Trust",
  "Bradford District Care NHS Foundation Trust",
  "Cambridgeshire and Peterborough NHS Foundation Trust",
  "Central and North West London NHS Foundation Trust",
  "Cheshire and Wirral Partnership NHS Foundation Trust",
  "Cornwall Partnership NHS Foundation Trust",
  "Coventry and Warwickshire Partnership NHS Trust",
  "Cumbria, Northumberland, Tyne and Wear NHS Foundation Trust",
  "Derbyshire Healthcare NHS Foundation Trust",
  "Devon Partnership NHS Trust",
  "Dorset HealthCare University NHS Foundation Trust",
  "East London NHS Foundation Trust",
  "Essex Partnership University NHS Foundation Trust",
  "Gloucestershire Health and Care NHS Foundation Trust",
  "Greater Manchester Mental Health NHS Foundation Trust",
  "Hampshire and Isle of Wight Healthcare NHS Foundation Trust",
  "Herefordshire and Worcestershire Health and Care NHS Trust",
  "Hertfordshire Partnership University NHS Foundation Trust",
  "Humber Teaching NHS Foundation Trust",
  "Kent and Medway NHS and Social Care Partnership Trust",
  "Lancashire and South Cumbria NHS Foundation Trust",
  "Leeds and York Partnership NHS Foundation Trust",
  "Leicestershire Partnership NHS Trust",
  "Lincolnshire Partnership NHS Foundation Trust",
  "Mersey Care NHS Foundation Trust",
  "Midlands Partnership University NHS Foundation Trust",
  "Norfolk and Suffolk NHS Foundation Trust",
  "North East London NHS Foundation Trust",
  "North London NHS Foundation Trust",
  "North Staffordshire Combined Healthcare NHS Trust",
  "Northamptonshire Healthcare NHS Foundation Trust",
  "Nottinghamshire Healthcare NHS Foundation Trust",
  "Oxford Health NHS Foundation Trust",
  "Oxleas NHS Foundation Trust",
  "Pennine Care NHS Foundation Trust",
  "Rotherham Doncaster and South Humber NHS Foundation Trust",
  "Sheffield Health and Social Care NHS Foundation Trust",
  "Somerset NHS Foundation Trust",
  "South London and Maudsley NHS Foundation Trust",
  "South West London and St George's Mental Health NHS Trust",
  "South West Yorkshire Partnership NHS Foundation Trust",
  "Surrey and Borders Partnership NHS Foundation Trust",
  "Sussex Partnership NHS Foundation Trust",
  "Tees, Esk and Wear Valleys NHS Foundation Trust",
  "West London NHS Trust",

  // Recently-merged predecessors (kept for historical data)
  "Barnet, Enfield and Haringey Mental Health NHS Trust",
  "Camden and Islington NHS Foundation Trust",
  "Solent NHS Trust",
  "Southern Health NHS Foundation Trust",

  // Name variants seen in our data
  "Kent and Medway Mental Health NHS Trust",

  // Independent-sector MH providers that take detained patients
  "Cygnet Health Care",
  "Elysium Healthcare",
  "Priory Group",
  "Navigo Health and Social Care CIC",
];

function normalise(name: string): string {
  return name
    .normalize("NFD") // decompose accented letters (e.g. é -> e + accent)
    .replace(/[\u0300-\u036f]/g, "") // strip the diacritics
    .toLowerCase()
    .replace(/&/g, " and ") // treat "&" and "and" alike
    .replace(/['‘’`]/g, "") // drop apostrophes entirely (St George's -> st georges)
    .replace(/[^a-z0-9]+/g, " ") // collapse other punctuation/whitespace to one space
    .trim();
}

const MH_SET = new Set(MENTAL_HEALTH_TRUSTS.map(normalise));

/** True if `trustName` is a mental-health provider (vs an acute/general one). */
export function isMentalHealthTrust(
  trustName: string | null | undefined,
): boolean {
  if (trustName === null || trustName === undefined || trustName === "") {
    return false;
  }
  return MH_SET.has(normalise(trustName));
}
